/**
 * TechnicianActionSummary Component - Industry-Grade Work Order Display
 * Professional, field-ready action summaries for technicians
 * Designed for mobile/tablet use with clear visual hierarchy and safety-first approach
 */

import { useState, useMemo } from 'react'
import {
  Wrench,
  AlertTriangle,
  CheckCircle,
  Clock,
  Thermometer,
  Gauge,
  Activity,
  TrendingUp,
  TrendingDown,
  Target,
  Zap,
  Shield,
  ChevronDown,
  ChevronRight,
  ArrowRight,
  Clipboard,
  User,
  Calendar,
  MapPin,
  AlertCircle,
  Info,
  Star,
  FileText,
  HardHat,
  Hammer,
  Timer,
  CircleDot,
  PlayCircle,
  ClipboardList,
  Sparkles,
  Eye,
  Minus
} from 'lucide-react'
import clsx from 'clsx'

// KPI Finding structure
interface KPIFinding {
  id: string
  kpiName: string
  currentValue: number
  targetValue: number
  unit: string
  status: 'critical' | 'warning' | 'normal' | 'optimal'
  trend: 'up' | 'down' | 'stable'
  trendPercent: number
  category: 'temperature' | 'pressure' | 'vibration' | 'flow' | 'electrical' | 'gas' | 'efficiency'
  sensorId?: string
  lastUpdated: string
  interpretation?: string
}

// Action item for technicians
interface ActionItem {
  id: string
  priority: 'immediate' | 'high' | 'medium' | 'low'
  action: string
  description: string
  estimatedTime: string
  requiredTools?: string[]
  safetyNotes?: string[]
  relatedKPIs: string[]
  status: 'pending' | 'in_progress' | 'completed' | 'deferred'
  assignee?: string
  dueDate?: string
  stepNumber?: number
}

// Root cause analysis
interface RootCauseAnalysis {
  primaryCause: string
  confidence: number
  contributingFactors: Array<{
    factor: string
    weight: number
    evidence: string
  }>
  historicalPattern?: string
}

interface TechnicianActionSummaryProps {
  assetId: string
  assetName: string
  location?: string
  findings: KPIFinding[]
  actions: ActionItem[]
  rootCause?: RootCauseAnalysis
  aiConfidence: number
  lastAnalysis: string
  technicianName?: string
  shift?: string
  workOrderNumber?: string
  className?: string
  onActionComplete?: (actionId: string) => void
  onActionDefer?: (actionId: string, reason: string) => void
  onActionStart?: (actionId: string) => void
}

export default function TechnicianActionSummary({
  assetId,
  assetName,
  location,
  findings,
  actions,
  rootCause,
  aiConfidence,
  lastAnalysis,
  technicianName,
  shift,
  workOrderNumber,
  className,
  onActionComplete,
  onActionDefer,
  onActionStart
}: TechnicianActionSummaryProps) {
  const [expandedAction, setExpandedAction] = useState<string | null>(null)
  const [expandedSection, setExpandedSection] = useState<'safety' | 'findings' | 'actions' | 'root_cause' | null>('actions')
  const [completedActions, setCompletedActions] = useState<Set<string>>(new Set())
  const [inProgressAction, setInProgressAction] = useState<string | null>(null)

  // Categorize and prioritize findings
  const categorizedFindings = useMemo(() => {
    const critical = findings.filter(f => f.status === 'critical')
    const warning = findings.filter(f => f.status === 'warning')
    const normal = findings.filter(f => f.status === 'normal' || f.status === 'optimal')
    return { critical, warning, normal }
  }, [findings])

  // Check if there are any safety concerns
  const hasSafetyConcerns = useMemo(() => {
    return actions.some(a => a.safetyNotes && a.safetyNotes.length > 0) ||
           categorizedFindings.critical.length > 0
  }, [actions, categorizedFindings])

  // Generate dynamic narrative based on findings
  const dynamicNarrative = useMemo(() => {
    const criticalCount = categorizedFindings.critical.length
    const warningCount = categorizedFindings.warning.length

    if (criticalCount > 0) {
      const criticalKPIs = categorizedFindings.critical.map(f => f.kpiName).join(', ')
      return {
        urgency: 'critical' as const,
        headline: 'STOP - SAFETY CHECK REQUIRED',
        summary: `${criticalCount} critical finding${criticalCount > 1 ? 's' : ''} detected. Review safety protocols before proceeding.`,
        details: `Affected: ${criticalKPIs}`,
        recommendation: 'Verify lockout/tagout procedures are in place. Assess immediate risks before any maintenance activity.',
        icon: AlertTriangle
      }
    } else if (warningCount > 0) {
      return {
        urgency: 'warning' as const,
        headline: 'ATTENTION - REVIEW REQUIRED',
        summary: `${warningCount} parameter${warningCount > 1 ? 's' : ''} outside optimal range detected.`,
        details: categorizedFindings.warning.map(f => f.kpiName).join(', '),
        recommendation: 'Address these items during scheduled maintenance to prevent escalation.',
        icon: AlertCircle
      }
    } else {
      return {
        urgency: 'normal' as const,
        headline: 'SYSTEM NORMAL',
        summary: 'All parameters within acceptable operating range.',
        details: `${findings.length} KPIs monitored`,
        recommendation: 'Proceed with routine inspection and preventive maintenance tasks.',
        icon: CheckCircle
      }
    }
  }, [categorizedFindings, findings.length])

  // Get all safety notes consolidated
  const allSafetyNotes = useMemo(() => {
    const notes = new Set<string>()
    actions.forEach(a => {
      a.safetyNotes?.forEach(note => notes.add(note))
    })
    return Array.from(notes)
  }, [actions])

  // Get all required tools consolidated
  const allRequiredTools = useMemo(() => {
    const tools = new Set<string>()
    actions.forEach(a => {
      a.requiredTools?.forEach(tool => tools.add(tool))
    })
    return Array.from(tools)
  }, [actions])

  // Priority configuration
  const getPriorityConfig = (priority: ActionItem['priority']) => {
    switch (priority) {
      case 'immediate':
        return {
          bg: 'bg-red-500/15',
          border: 'border-red-500/40',
          text: 'text-red-400',
          badgeBg: 'bg-red-500',
          badgeText: 'text-white',
          icon: AlertTriangle,
          label: 'IMMEDIATE',
          description: 'Do this now'
        }
      case 'high':
        return {
          bg: 'bg-amber-500/15',
          border: 'border-amber-500/40',
          text: 'text-amber-400',
          badgeBg: 'bg-amber-500',
          badgeText: 'text-black',
          icon: AlertCircle,
          label: 'HIGH',
          description: 'Complete today'
        }
      case 'medium':
        return {
          bg: 'bg-cyan-500/15',
          border: 'border-cyan-500/40',
          text: 'text-cyan-400',
          badgeBg: 'bg-cyan-500',
          badgeText: 'text-black',
          icon: Info,
          label: 'MEDIUM',
          description: 'This week'
        }
      default:
        return {
          bg: 'bg-surface-700/50',
          border: 'border-surface-600',
          text: 'text-surface-400',
          badgeBg: 'bg-surface-600',
          badgeText: 'text-surface-200',
          icon: CircleDot,
          label: 'LOW',
          description: 'When possible'
        }
    }
  }

  // Status configuration
  const getStatusConfig = (status: KPIFinding['status']) => {
    switch (status) {
      case 'critical':
        return {
          bg: 'bg-red-500/10',
          border: 'border-red-500/30',
          text: 'text-red-400',
          icon: AlertTriangle,
          label: 'CRITICAL'
        }
      case 'warning':
        return {
          bg: 'bg-amber-500/10',
          border: 'border-amber-500/30',
          text: 'text-amber-400',
          icon: AlertCircle,
          label: 'WARNING'
        }
      case 'optimal':
        return {
          bg: 'bg-emerald-500/10',
          border: 'border-emerald-500/30',
          text: 'text-emerald-400',
          icon: Star,
          label: 'OPTIMAL'
        }
      default:
        return {
          bg: 'bg-surface-800/50',
          border: 'border-surface-700',
          text: 'text-surface-300',
          icon: CheckCircle,
          label: 'NORMAL'
        }
    }
  }

  // Category icon
  const getCategoryIcon = (category: KPIFinding['category']) => {
    switch (category) {
      case 'temperature': return Thermometer
      case 'pressure': return Gauge
      case 'vibration': return Activity
      case 'flow': return TrendingUp
      case 'electrical': return Zap
      case 'gas': return Activity
      default: return Target
    }
  }

  // Handle action status changes
  const handleStartAction = (actionId: string) => {
    setInProgressAction(actionId)
    onActionStart?.(actionId)
  }

  const handleCompleteAction = (actionId: string) => {
    setCompletedActions(prev => new Set([...prev, actionId]))
    if (inProgressAction === actionId) setInProgressAction(null)
    onActionComplete?.(actionId)
  }

  // Calculate progress
  const progressStats = useMemo(() => {
    const total = actions.length
    const completed = actions.filter(a => a.status === 'completed' || completedActions.has(a.id)).length
    const inProgress = inProgressAction ? 1 : 0
    const remaining = total - completed - inProgress
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0

    return { total, completed, inProgress, remaining, percentage }
  }, [actions, completedActions, inProgressAction])

  // Estimate total time
  const totalEstimatedTime = useMemo(() => {
    let totalMinutes = 0
    actions.forEach(a => {
      const match = a.estimatedTime.match(/(\d+)\s*(min|hour|hr|h)/i)
      if (match) {
        const num = parseInt(match[1])
        const unit = match[2].toLowerCase()
        totalMinutes += unit.startsWith('h') ? num * 60 : num
      }
    })
    if (totalMinutes >= 60) {
      const hours = Math.floor(totalMinutes / 60)
      const mins = totalMinutes % 60
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
    }
    return `${totalMinutes}m`
  }, [actions])

  return (
    <div className={clsx('rounded-2xl border border-surface-700 overflow-hidden bg-gradient-to-b from-surface-900 to-surface-900/95 shadow-xl', className)}>
      {/* ═══════════════════════════════════════════════════════════════════════
          WORK ORDER HEADER
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className="relative">
        {/* Background gradient based on urgency */}
        <div className={clsx(
          'absolute inset-0',
          dynamicNarrative.urgency === 'critical' && 'bg-gradient-to-r from-red-500/20 via-red-500/10 to-transparent',
          dynamicNarrative.urgency === 'warning' && 'bg-gradient-to-r from-amber-500/20 via-amber-500/10 to-transparent',
          dynamicNarrative.urgency === 'normal' && 'bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent'
        )} />

        <div className="relative p-5">
          {/* Work Order Badge */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={clsx(
                'p-2.5 rounded-xl',
                dynamicNarrative.urgency === 'critical' && 'bg-red-500/20',
                dynamicNarrative.urgency === 'warning' && 'bg-amber-500/20',
                dynamicNarrative.urgency === 'normal' && 'bg-emerald-500/20'
              )}>
                <ClipboardList size={24} className={clsx(
                  dynamicNarrative.urgency === 'critical' && 'text-red-400',
                  dynamicNarrative.urgency === 'warning' && 'text-amber-400',
                  dynamicNarrative.urgency === 'normal' && 'text-emerald-400'
                )} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold text-surface-500 uppercase tracking-wider">Work Order</span>
                  {workOrderNumber && (
                    <span className="text-xs font-mono text-primary-400 px-2 py-0.5 rounded bg-primary-500/10 border border-primary-500/20">
                      #{workOrderNumber}
                    </span>
                  )}
                </div>
                <h1 className="text-xl font-bold text-white mt-1">{assetName}</h1>
              </div>
            </div>

            {/* AI Confidence Badge */}
            <div className="flex flex-col items-center p-3 rounded-xl bg-surface-800/50 border border-surface-700/50">
              <div className="flex items-center gap-1 mb-1">
                <Sparkles size={12} className="text-primary-400" />
                <span className="text-[9px] text-surface-500 uppercase tracking-wider">AI Analysis</span>
              </div>
              <span className={clsx(
                'text-2xl font-bold font-mono',
                aiConfidence >= 0.85 ? 'text-emerald-400' :
                aiConfidence >= 0.7 ? 'text-amber-400' : 'text-red-400'
              )}>
                {Math.round(aiConfidence * 100)}%
              </span>
              <span className="text-[9px] text-surface-500">confidence</span>
            </div>
          </div>

          {/* Asset Info Row */}
          <div className="flex flex-wrap items-center gap-4 text-xs text-surface-400">
            <span className="flex items-center gap-1.5 font-mono bg-surface-800/50 px-2.5 py-1 rounded-lg border border-surface-700/50">
              <FileText size={12} className="text-surface-500" />
              {assetId}
            </span>
            {location && (
              <span className="flex items-center gap-1.5 bg-surface-800/50 px-2.5 py-1 rounded-lg border border-surface-700/50">
                <MapPin size={12} className="text-surface-500" />
                {location}
              </span>
            )}
            <span className="flex items-center gap-1.5 bg-surface-800/50 px-2.5 py-1 rounded-lg border border-surface-700/50">
              <Clock size={12} className="text-surface-500" />
              {lastAnalysis}
            </span>
          </div>

          {/* Status Alert Box */}
          <div className={clsx(
            'mt-4 p-4 rounded-xl border',
            dynamicNarrative.urgency === 'critical' && 'bg-red-500/10 border-red-500/30',
            dynamicNarrative.urgency === 'warning' && 'bg-amber-500/10 border-amber-500/30',
            dynamicNarrative.urgency === 'normal' && 'bg-emerald-500/10 border-emerald-500/30'
          )}>
            <div className="flex items-start gap-3">
              <dynamicNarrative.icon size={24} className={clsx(
                dynamicNarrative.urgency === 'critical' && 'text-red-400',
                dynamicNarrative.urgency === 'warning' && 'text-amber-400',
                dynamicNarrative.urgency === 'normal' && 'text-emerald-400'
              )} />
              <div className="flex-1">
                <h2 className={clsx(
                  'font-bold text-lg',
                  dynamicNarrative.urgency === 'critical' && 'text-red-400',
                  dynamicNarrative.urgency === 'warning' && 'text-amber-400',
                  dynamicNarrative.urgency === 'normal' && 'text-emerald-400'
                )}>
                  {dynamicNarrative.headline}
                </h2>
                <p className="text-sm text-surface-300 mt-1">{dynamicNarrative.summary}</p>
                {dynamicNarrative.details && (
                  <p className="text-xs text-surface-500 mt-1">{dynamicNarrative.details}</p>
                )}
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-surface-700/50">
                  <ArrowRight size={14} className="text-primary-400" />
                  <p className="text-sm text-primary-400">{dynamicNarrative.recommendation}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          TECHNICIAN & PROGRESS BAR
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className="px-5 py-3 bg-surface-800/30 border-y border-surface-700/50">
        <div className="flex items-center justify-between">
          {/* Technician Info */}
          <div className="flex items-center gap-4">
            {technicianName && (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary-500/20 border border-primary-500/30 flex items-center justify-center">
                  <User size={14} className="text-primary-400" />
                </div>
                <div>
                  <p className="text-xs text-surface-500">Assigned to</p>
                  <p className="text-sm font-medium text-surface-200">{technicianName}</p>
                </div>
              </div>
            )}
            {shift && (
              <div className="flex items-center gap-2 text-xs text-surface-400">
                <Clock size={12} />
                {shift} Shift
              </div>
            )}
          </div>

          {/* Progress & Time */}
          <div className="flex items-center gap-6">
            <div className="text-right">
              <p className="text-xs text-surface-500">Est. Time</p>
              <p className="text-sm font-mono font-medium text-surface-200">{totalEstimatedTime}</p>
            </div>
            <div className="w-px h-8 bg-surface-700" />
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-xs text-surface-500">Progress</p>
                <p className="text-sm font-mono font-bold text-primary-400">{progressStats.percentage}%</p>
              </div>
              <div className="w-24 h-2.5 bg-surface-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-primary-500 to-primary-400 rounded-full transition-all duration-500"
                  style={{ width: `${progressStats.percentage}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Task Status Summary */}
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-surface-700/50">
          <div className="flex items-center gap-2 text-xs">
            <CheckCircle size={14} className="text-emerald-400" />
            <span className="text-surface-300">{progressStats.completed} completed</span>
          </div>
          {progressStats.inProgress > 0 && (
            <div className="flex items-center gap-2 text-xs">
              <PlayCircle size={14} className="text-amber-400" />
              <span className="text-surface-300">{progressStats.inProgress} in progress</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-xs">
            <CircleDot size={14} className="text-surface-500" />
            <span className="text-surface-400">{progressStats.remaining} remaining</span>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          SAFETY SECTION (Always visible if applicable)
      ═══════════════════════════════════════════════════════════════════════ */}
      {hasSafetyConcerns && (
        <div className="border-b border-surface-700/50">
          <button
            onClick={() => setExpandedSection(expandedSection === 'safety' ? null : 'safety')}
            className="w-full px-5 py-3 flex items-center justify-between hover:bg-surface-800/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/20 border border-red-500/30">
                <HardHat size={18} className="text-red-400" />
              </div>
              <div className="text-left">
                <span className="font-semibold text-red-400">Safety Requirements</span>
                <p className="text-xs text-surface-500 mt-0.5">
                  {allSafetyNotes.length} safety note{allSafetyNotes.length !== 1 ? 's' : ''} • Review before starting
                </p>
              </div>
            </div>
            <ChevronDown
              size={18}
              className={clsx('text-surface-400 transition-transform', expandedSection === 'safety' && 'rotate-180')}
            />
          </button>

          {expandedSection === 'safety' && (
            <div className="px-5 pb-4 space-y-3">
              {allSafetyNotes.map((note, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                  <Shield size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-surface-300">{note}</p>
                </div>
              ))}

              {/* Required Tools */}
              {allRequiredTools.length > 0 && (
                <div className="mt-4 pt-4 border-t border-surface-700/50">
                  <div className="flex items-center gap-2 mb-3">
                    <Hammer size={14} className="text-surface-400" />
                    <span className="text-xs font-semibold text-surface-400 uppercase tracking-wider">
                      Required Tools ({allRequiredTools.length})
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {allRequiredTools.map((tool, i) => (
                      <span key={i} className="px-3 py-1.5 text-xs rounded-lg bg-surface-800 text-surface-300 border border-surface-700">
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          KPI FINDINGS SECTION
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className="border-b border-surface-700/50">
        <button
          onClick={() => setExpandedSection(expandedSection === 'findings' ? null : 'findings')}
          className="w-full px-5 py-3 flex items-center justify-between hover:bg-surface-800/30 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary-500/20 border border-primary-500/30">
              <Eye size={18} className="text-primary-400" />
            </div>
            <div className="text-left">
              <span className="font-semibold text-surface-200">Sensor Readings & KPIs</span>
              <p className="text-xs text-surface-500 mt-0.5">{findings.length} parameters monitored</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {categorizedFindings.critical.length > 0 && (
              <span className="px-2 py-1 text-[10px] font-bold rounded-full bg-red-500/20 text-red-400 border border-red-500/30">
                {categorizedFindings.critical.length} CRITICAL
              </span>
            )}
            {categorizedFindings.warning.length > 0 && (
              <span className="px-2 py-1 text-[10px] font-bold rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                {categorizedFindings.warning.length} WARNING
              </span>
            )}
            <ChevronDown
              size={18}
              className={clsx('text-surface-400 transition-transform', expandedSection === 'findings' && 'rotate-180')}
            />
          </div>
        </button>

        {expandedSection === 'findings' && (
          <div className="px-5 pb-4 space-y-2">
            {findings.map((finding) => {
              const statusConfig = getStatusConfig(finding.status)
              const CategoryIcon = getCategoryIcon(finding.category)

              return (
                <div
                  key={finding.id}
                  className={clsx(
                    'p-4 rounded-xl border transition-all',
                    statusConfig.bg,
                    statusConfig.border
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={clsx('p-2 rounded-lg', statusConfig.bg)}>
                        <CategoryIcon size={18} className={statusConfig.text} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-surface-200">{finding.kpiName}</h4>
                          <span className={clsx(
                            'text-[9px] font-bold uppercase px-1.5 py-0.5 rounded',
                            statusConfig.bg, statusConfig.text, 'border', statusConfig.border
                          )}>
                            {statusConfig.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-surface-500">
                          <span className="capitalize">{finding.category}</span>
                          {finding.sensorId && (
                            <>
                              <span>•</span>
                              <span className="font-mono">{finding.sensorId}</span>
                            </>
                          )}
                          <span>•</span>
                          <span>{finding.lastUpdated}</span>
                        </div>
                        {finding.interpretation && (
                          <p className="text-sm text-surface-400 mt-2 italic">{finding.interpretation}</p>
                        )}
                      </div>
                    </div>

                    {/* Value Display */}
                    <div className="text-right">
                      <div className="flex items-center gap-2">
                        <span className={clsx('text-2xl font-bold font-mono', statusConfig.text)}>
                          {finding.currentValue}
                        </span>
                        <span className="text-sm text-surface-500">{finding.unit}</span>
                        {finding.trend === 'up' && <TrendingUp size={16} className="text-red-400" />}
                        {finding.trend === 'down' && <TrendingDown size={16} className="text-emerald-400" />}
                        {finding.trend === 'stable' && <Minus size={16} className="text-surface-500" />}
                      </div>
                      <div className="text-xs text-surface-500 mt-1">
                        Target: <span className="font-mono">{finding.targetValue} {finding.unit}</span>
                      </div>
                      <div className={clsx(
                        'text-xs font-mono mt-1',
                        finding.trendPercent > 0 ? 'text-red-400' : finding.trendPercent < 0 ? 'text-emerald-400' : 'text-surface-500'
                      )}>
                        {finding.trendPercent > 0 ? '+' : ''}{finding.trendPercent}% from target
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          ACTION ITEMS SECTION (Main Focus)
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className="border-b border-surface-700/50">
        <button
          onClick={() => setExpandedSection(expandedSection === 'actions' ? null : 'actions')}
          className="w-full px-5 py-3 flex items-center justify-between hover:bg-surface-800/30 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20 border border-emerald-500/30">
              <Wrench size={18} className="text-emerald-400" />
            </div>
            <div className="text-left">
              <span className="font-semibold text-surface-200">Action Checklist</span>
              <p className="text-xs text-surface-500 mt-0.5">
                {progressStats.completed}/{progressStats.total} completed
              </p>
            </div>
          </div>
          <ChevronDown
            size={18}
            className={clsx('text-surface-400 transition-transform', expandedSection === 'actions' && 'rotate-180')}
          />
        </button>

        {expandedSection === 'actions' && (
          <div className="px-5 pb-4 space-y-3">
            {actions.map((action, index) => {
              const priorityConfig = getPriorityConfig(action.priority)
              const isExpanded = expandedAction === action.id
              const isCompleted = action.status === 'completed' || completedActions.has(action.id)
              const isInProgress = inProgressAction === action.id

              return (
                <div
                  key={action.id}
                  className={clsx(
                    'rounded-xl border overflow-hidden transition-all',
                    isCompleted ? 'bg-emerald-500/5 border-emerald-500/20 opacity-75' :
                    isInProgress ? 'bg-amber-500/10 border-amber-500/30 ring-2 ring-amber-500/20' :
                    priorityConfig.bg + ' ' + priorityConfig.border
                  )}
                >
                  {/* Action Header */}
                  <div
                    className="p-4 cursor-pointer"
                    onClick={() => setExpandedAction(isExpanded ? null : action.id)}
                  >
                    <div className="flex items-start gap-3">
                      {/* Step Number / Status */}
                      <div className={clsx(
                        'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-lg',
                        isCompleted ? 'bg-emerald-500 text-white' :
                        isInProgress ? 'bg-amber-500 text-black' :
                        'bg-surface-700 text-surface-300'
                      )}>
                        {isCompleted ? <CheckCircle size={20} /> :
                         isInProgress ? <PlayCircle size={20} /> :
                         action.stepNumber || index + 1}
                      </div>

                      {/* Action Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={clsx(
                            'text-[9px] font-bold uppercase px-2 py-0.5 rounded',
                            isCompleted ? 'bg-emerald-500/20 text-emerald-400' :
                            isInProgress ? 'bg-amber-500/20 text-amber-400' :
                            priorityConfig.badgeBg + ' ' + priorityConfig.badgeText
                          )}>
                            {isCompleted ? 'COMPLETED' : isInProgress ? 'IN PROGRESS' : priorityConfig.label}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-surface-500">
                            <Timer size={10} />
                            {action.estimatedTime}
                          </span>
                          {action.dueDate && (
                            <span className="flex items-center gap-1 text-xs text-surface-500">
                              <Calendar size={10} />
                              {action.dueDate}
                            </span>
                          )}
                        </div>
                        <h4 className={clsx(
                          'font-semibold mt-2 text-base',
                          isCompleted ? 'text-surface-400 line-through' : 'text-surface-100'
                        )}>
                          {action.action}
                        </h4>
                        <p className={clsx(
                          'text-sm mt-1',
                          isCompleted ? 'text-surface-500' : 'text-surface-400'
                        )}>
                          {action.description}
                        </p>
                      </div>

                      {/* Expand Arrow */}
                      <ChevronRight
                        size={20}
                        className={clsx(
                          'text-surface-500 transition-transform flex-shrink-0',
                          isExpanded && 'rotate-90'
                        )}
                      />
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && !isCompleted && (
                    <div className="px-4 pb-4 pt-2 border-t border-surface-700/30 bg-surface-900/30">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Required Tools */}
                        {action.requiredTools && action.requiredTools.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Hammer size={12} className="text-surface-400" />
                              <span className="text-[10px] font-bold text-surface-400 uppercase tracking-wider">
                                Tools Needed
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {action.requiredTools.map((tool, i) => (
                                <span key={i} className="px-2 py-1 text-xs rounded bg-surface-800 text-surface-300 border border-surface-700">
                                  {tool}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Related KPIs */}
                        {action.relatedKPIs.length > 0 && (
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Target size={12} className="text-surface-400" />
                              <span className="text-[10px] font-bold text-surface-400 uppercase tracking-wider">
                                Related KPIs
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {action.relatedKPIs.map((kpi, i) => (
                                <span key={i} className="px-2 py-1 text-xs rounded bg-primary-500/10 text-primary-400 border border-primary-500/20">
                                  {kpi}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Safety Notes for this action */}
                      {action.safetyNotes && action.safetyNotes.length > 0 && (
                        <div className="mt-4 p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                          <div className="flex items-center gap-2 mb-2">
                            <Shield size={12} className="text-red-400" />
                            <span className="text-[10px] font-bold text-red-400 uppercase tracking-wider">
                              Safety Notes
                            </span>
                          </div>
                          <ul className="space-y-1.5">
                            {action.safetyNotes.map((note, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-surface-300">
                                <span className="text-red-400 mt-1">•</span>
                                {note}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex items-center gap-3 mt-4 pt-4 border-t border-surface-700/30">
                        {!isInProgress ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleStartAction(action.id)
                            }}
                            className="flex-1 py-2.5 px-4 rounded-lg bg-primary-500/20 text-primary-400 border border-primary-500/30 font-medium hover:bg-primary-500/30 transition-colors flex items-center justify-center gap-2"
                          >
                            <PlayCircle size={16} />
                            Start Task
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleCompleteAction(action.id)
                            }}
                            className="flex-1 py-2.5 px-4 rounded-lg bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-medium hover:bg-emerald-500/30 transition-colors flex items-center justify-center gap-2"
                          >
                            <CheckCircle size={16} />
                            Mark Complete
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            onActionDefer?.(action.id, 'Deferred by technician')
                          }}
                          className="py-2.5 px-4 rounded-lg bg-surface-700/50 text-surface-400 border border-surface-600 hover:bg-surface-700 transition-colors"
                        >
                          Defer
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          ROOT CAUSE ANALYSIS SECTION
      ═══════════════════════════════════════════════════════════════════════ */}
      {rootCause && (
        <div>
          <button
            onClick={() => setExpandedSection(expandedSection === 'root_cause' ? null : 'root_cause')}
            className="w-full px-5 py-3 flex items-center justify-between hover:bg-surface-800/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20 border border-purple-500/30">
                <Target size={18} className="text-purple-400" />
              </div>
              <div className="text-left">
                <span className="font-semibold text-surface-200">Root Cause Analysis</span>
                <p className="text-xs text-surface-500 mt-0.5">
                  {Math.round(rootCause.confidence * 100)}% confidence • AI-generated
                </p>
              </div>
            </div>
            <ChevronDown
              size={18}
              className={clsx('text-surface-400 transition-transform', expandedSection === 'root_cause' && 'rotate-180')}
            />
          </button>

          {expandedSection === 'root_cause' && (
            <div className="px-5 pb-4">
              <div className="p-4 rounded-xl bg-purple-500/5 border border-purple-500/20">
                <h4 className="font-semibold text-lg text-surface-100 mb-2">{rootCause.primaryCause}</h4>
                {rootCause.historicalPattern && (
                  <p className="text-sm text-surface-400 mb-4 italic border-l-2 border-purple-500/30 pl-3">
                    "{rootCause.historicalPattern}"
                  </p>
                )}

                <div className="mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Activity size={14} className="text-purple-400" />
                    <span className="text-xs font-bold text-surface-400 uppercase tracking-wider">
                      Contributing Factors
                    </span>
                  </div>
                  <div className="space-y-3">
                    {rootCause.contributingFactors.map((factor, i) => (
                      <div key={i} className="flex items-center gap-4">
                        <div className="w-full max-w-[200px]">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-surface-300">{factor.factor}</span>
                            <span className="text-xs font-mono text-purple-400">{Math.round(factor.weight * 100)}%</span>
                          </div>
                          <div className="h-2 bg-surface-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full"
                              style={{ width: `${factor.weight * 100}%` }}
                            />
                          </div>
                        </div>
                        <p className="flex-1 text-xs text-surface-500">{factor.evidence}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          FOOTER ACTIONS
      ═══════════════════════════════════════════════════════════════════════ */}
      <div className="px-5 py-4 bg-surface-800/30 border-t border-surface-700/50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-700/50 text-surface-300 hover:bg-surface-700 transition-colors text-sm">
            <Clipboard size={14} />
            Copy Summary
          </button>
          <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-700/50 text-surface-300 hover:bg-surface-700 transition-colors text-sm">
            <FileText size={14} />
            Export PDF
          </button>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500/20 text-primary-400 border border-primary-500/30 hover:bg-primary-500/30 transition-colors font-medium text-sm">
          <Calendar size={14} />
          Schedule Follow-up
        </button>
      </div>
    </div>
  )
}
